import type { ReactNode } from "react";
import { createContext, useContext, useMemo } from "react";

import { CommunitySwitcherContent } from "../components/CommunitySwitcherContent";
import type {
  CommunitySwitcherApi,
  CommunitySwitcherFeatureFlags,
  CommunitySwitcherThemeAdapter,
  GetNotificationCount,
  TranslateFn,
} from "../types/adapters";
import type { CommunitySwitcher } from "../types/CommunitySwitcher";

export type { CommunitySwitcher };

export interface CommunitySwitcherContextValue {
  api: CommunitySwitcherApi;
  theme: CommunitySwitcherThemeAdapter;
  t: TranslateFn;
  getNotificationCount: GetNotificationCount;
  featureFlags?: CommunitySwitcherFeatureFlags;
  setCommunitiesNeedingCounts?: (communities: CommunitySwitcher[]) => void;
  isLoadingAppData?: boolean;
  backgroundColor?: string;
}

const CommunitySwitcherContext =
  createContext<CommunitySwitcherContextValue | null>(null);

export interface CommunitySwitcherProviderProps {
  readonly value: CommunitySwitcherContextValue;
  /**
   * Content rendered inside the provider. Defaults to `CommunitySwitcherContent`
   * (the desktop sidebar) when omitted, so existing consumers are unaffected.
   * Pass an alternative presentation (e.g. `CommunitySwitcherDrawerContent`)
   * to reuse the same adapter for a different surface.
   */
  readonly children?: ReactNode;
}

export function CommunitySwitcherProvider({
  value,
  children,
}: CommunitySwitcherProviderProps) {
  const ctx = useMemo(() => value, [value]);
  return (
    <CommunitySwitcherContext.Provider value={ctx}>
      {children ?? <CommunitySwitcherContent />}
    </CommunitySwitcherContext.Provider>
  );
}

export function useCommunitySwitcherContext(): CommunitySwitcherContextValue {
  const ctx = useContext(CommunitySwitcherContext);
  if (!ctx) {
    throw new Error(
      "useCommunitySwitcherContext must be used within CommunitySwitcherProvider",
    );
  }
  return ctx;
}
